/**
 * CR-6441: Dependent-filter cascade logic for the Purchasing → ASN filter panel.
 *
 * Implements the same cascade behaviour as PO Headers and Forecast tabs:
 *   - Division → Site (filter sites by selected division)
 *   - Company Country → Site (filter sites by selected country)
 *   - Site → Supplier (filter suppliers by selected site/customer)
 *   - Supplier Country → Supplier (filter suppliers by selected country)
 *   - Supplier Group → Supplier (filter suppliers by selected group)
 *   - Commodity Group → Commodity Type (filter types by selected group)
 *
 * The customer view panel uses these cascades to ensure filter dependencies
 * work correctly and maintain consistency with other Purchasing tabs.
 */
import { useMemo } from 'react';

export interface ASNCascadeFilterOptions {
  suppliers: Array<{
    id: number;
    name: string;
    group?: number | null;
    customers?: number[];  // Site IDs this supplier serves
    country?: string | null;  // Supplier's country for filtering
  }>;
  commodityGroups: Array<{ id: number; name: string }>;
  commodityTypes: Array<{ id: number; name: string; commodity_group: number }>;
  supplierGroups: Array<{ id: number; name: string }>;
  sites: Array<{
    id: number;
    name: string;
    group?: number | null;  // Division ID
    country?: string | null;
  }>;
  divisions: Array<{ id: number; name: string }>;
  siteCountries: string[];
  supplierCountries: string[];
}

export interface ASNFilters {
  asnNumber: string;
  partSearch: string;
  supplier: string;
  trackingNumber: string;
  poNumber: string;
  poLineNumber: string;
  partNumber: string;
  description: string;
  status: string;
  courier: string;
  courierDeliveryStatus: string;
  dateFrom: string;
  dateTo: string;
  site: string;
  warehouse: string;
  supplierCountry: string;
  supplierGroup: string;
  commodityGroup: string;
  commodityType: string;
  strategic: string;
  // CR-6441: New cascade filter fields
  division: string;
  companyCountry: string;
}

export function useASNFilterCascades(
  filters: ASNFilters,
  filterOptions: ASNCascadeFilterOptions | null,
  onFilterChange: (key: string, value: string) => void,
) {
  const suppliers = filterOptions?.suppliers ?? [];
  const commodityTypes = filterOptions?.commodityTypes ?? [];
  const sites = filterOptions?.sites ?? [];

  // Site options: narrowed by Division and/or Company Country.
  const siteOptions = useMemo(() => {
    let filtered = sites;

    // Filter by division if selected
    if (filters.division) {
      filtered = filtered.filter(
        (s) => String(s.group ?? '') === filters.division,
      );
    }

    // Filter by company country if selected
    if (filters.companyCountry) {
      filtered = filtered.filter(
        (s) => s.country === filters.companyCountry,
      );
    }

    // Keep currently selected site in the list even if it doesn't match
    if (filters.site && !filtered.some((s) => String(s.id) === filters.site)) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected) return [selected, ...filtered];
    }
    return filtered;
  }, [sites, filters.division, filters.companyCountry, filters.site]);

  // Supplier options: narrowed by Site, Supplier Country, and/or Supplier Group.
  const supplierSiteOptions = useMemo(() => {
    let filtered = suppliers;

    // Filter by site if selected
    if (filters.site) {
      const siteId = parseInt(filters.site, 10);
      filtered = filtered.filter(
        (s) => s.customers?.includes(siteId) ?? false,
      );
    }

    // Filter by supplier country if selected
    if (filters.supplierCountry) {
      filtered = filtered.filter(
        (s) => s.country === filters.supplierCountry,
      );
    }

    // Filter by supplier group if selected
    if (filters.supplierGroup) {
      filtered = filtered.filter(
        (s) => String(s.group ?? '') === filters.supplierGroup,
      );
    }

    // Keep currently selected supplier (by name match) in the list
    if (filters.supplier && !filtered.some((s) => s.name.toLowerCase().includes(filters.supplier.toLowerCase()))) {
      const selected = suppliers.find((s) => s.name.toLowerCase().includes(filters.supplier.toLowerCase()));
      if (selected) return [selected, ...filtered];
    }
    return filtered;
  }, [suppliers, filters.site, filters.supplierCountry, filters.supplierGroup, filters.supplier]);

  // Commodity Type: narrowed by the selected Commodity Group.
  const commodityTypeOptions = useMemo(() => {
    if (!filters.commodityGroup) {
      return commodityTypes;
    }
    return commodityTypes.filter(
      (t) => String(t.commodity_group) === filters.commodityGroup,
    );
  }, [commodityTypes, filters.commodityGroup]);

  // --- Cascade change handlers (clear now-invalid dependents) -------------

  /**
   * When Division changes, clear Site if it doesn't belong to the new division.
   */
  const handleDivisionChange = (value: string) => {
    onFilterChange('division', value);
    if (value && filters.site) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected && String(selected.group ?? '') !== value) {
        onFilterChange('site', '');
        // Also clear supplier since site was cleared
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Company Country changes, clear Site if it doesn't match the new country.
   */
  const handleCompanyCountryChange = (value: string) => {
    onFilterChange('companyCountry', value);
    if (value && filters.site) {
      const selected = sites.find((s) => String(s.id) === filters.site);
      if (selected && selected.country !== value) {
        onFilterChange('site', '');
        // Also clear supplier since site was cleared
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Site changes, clear Supplier if it doesn't serve the new site.
   */
  const handleSiteChange = (value: string) => {
    onFilterChange('site', value);
    if (value && filters.supplier) {
      const siteId = parseInt(value, 10);
      // For text-based supplier filter, we check if any matching supplier serves this site
      const matchingSuppliers = suppliers.filter((s) =>
        s.name.toLowerCase().includes(filters.supplier.toLowerCase()),
      );
      const anyServesSite = matchingSuppliers.some(
        (s) => s.customers?.includes(siteId) ?? false,
      );
      if (!anyServesSite) {
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Supplier Country changes, clear Supplier if it doesn't match the new country.
   * @param value - The new country value
   * @param currentSupplier - The current supplier text (passed from component to avoid stale closure)
   */
  const handleSupplierCountryChange = (value: string, currentSupplier?: string) => {
    onFilterChange('supplierCountry', value);
    // Use currentSupplier if provided (from component), otherwise fall back to filters
    const supplierText = currentSupplier ?? filters.supplier;
    // Clear supplier filter if the text doesn't match any supplier in the new country
    if (value && supplierText) {
      const matchingSuppliers = suppliers.filter(
        (s) => s.name.toLowerCase().includes(supplierText.toLowerCase()) && s.country === value,
      );
      if (matchingSuppliers.length === 0) {
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Supplier Group changes, clear Supplier if it doesn't belong to the new group.
   * @param value - The new group value
   * @param currentSupplier - The current supplier text (passed from component to avoid stale closure)
   */
  const handleSupplierGroupChange = (value: string, currentSupplier?: string) => {
    onFilterChange('supplierGroup', value);
    // Use currentSupplier if provided (from component), otherwise fall back to filters
    const supplierText = currentSupplier ?? filters.supplier;
    if (value && supplierText) {
      const matchingSuppliers = suppliers.filter(
        (s) => s.name.toLowerCase().includes(supplierText.toLowerCase()) && String(s.group ?? '') === value,
      );
      if (matchingSuppliers.length === 0) {
        onFilterChange('supplier', '');
      }
    }
  };

  /**
   * When Commodity Group changes, clear Commodity Type if it doesn't belong to the new group.
   */
  const handleCommodityGroupChange = (value: string) => {
    onFilterChange('commodityGroup', value);
    if (value && filters.commodityType) {
      const selected = commodityTypes.find(
        (t) => String(t.id) === filters.commodityType,
      );
      if (selected && String(selected.commodity_group) !== value) {
        onFilterChange('commodityType', '');
      }
    }
  };

  return {
    siteOptions,
    supplierSiteOptions,
    commodityTypeOptions,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  };
}
